import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import {
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    TextArea,
} from "@jhool-io/fe-components";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";

import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import {
    IEditSupervisorCheckInNotePayload,
    IGetClientNotes,
    ISessionNote,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";

interface EditSupervisorCheckInNoteProps {
    noteDetails: ISessionNote;
}

export default function EditSupervisorCheckInNote({
    noteDetails,
}: EditSupervisorCheckInNoteProps) {
    const [showPrompt, setShowPrompt] = React.useState(false);
    const [selectedNote, setSelectedNote] = React.useState(
        {} as IGetClientNotes
    );

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const isSigning = React.useRef(false);

    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        watch,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditSupervisorCheckInNotePayload>({
        resolver: yupResolver(
            yup.object({
                update: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: {
            update: noteDetails?.note_content?.update || "",
            signature: noteDetails?.note_content?.signature || "",
        },
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue("update", note.note_content.update_note_content);
        setShowPrompt(false);
    };

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}

            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="edit-form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <TextArea
                    {...register("update")}
                    label="General update"
                    errorText={errors.update?.message}
                    hasError={!!errors.update}
                    isRequired
                />

                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
